
  import Vue from "vue";

  import Component from "vue-class-component";
  import {Getter} from "vuex-class";
  import {HeaderGetters, HeaderModule} from "@/store/modules/header";
  import {Watch} from "vue-property-decorator";
  import StatisticEmployeesV2 from "./StatisticEmployeesV2.vue";
  import StatisticServiceV2 from "@/service/StatisticServiceV2";
  import StatisticOrderSummariesTable from "@/views/Statistic/StatisticOrderSummariesTable.vue";
  import {OrderTypes} from "@/enums";
  import {Moment} from "moment";
  import StatisticReports from "@/views/Statistic/StatisticReports.vue";
  import StatisticPerformance from "@/views/Statistic/StatisticPerformance.vue";
  import {OrderSummaries, OrderSummariesMetrics, OrderSummariesOrderTypeGroup} from "@/model/OrderSummaries";

  /**
   * Flattens v2 metrics into the row shape the order summaries table displays.
   */
  function toRow(order: any, metrics: OrderSummariesMetrics) {
    return {
      order: order,
      minutes: metrics.minutes,
      costs: metrics.wageCosts,
      costsWithPayrollTaxes: metrics.laborCosts,
      costsTravel: metrics.travelCosts,
      costsPassenger: metrics.passengerCosts,
      perDiemTotal: metrics.perDiems.total,
      earnings: metrics.earnings,
      expenses: metrics.expenses,
      profit: metrics.profit,
      averageWage: metrics.averageWage,
      productivity: metrics.productivity,
    };
  }

  const EMPTY_METRICS: OrderSummariesMetrics = {
    minutes: 0,
    wageCosts: 0,
    laborCosts: 0,
    travelCosts: 0,
    passengerCosts: 0,
    perDiems: {catering: 0, misc: 0, total: 0},
    earnings: 0,
    expenses: 0,
    profit: 0,
    averageWage: null,
    productivity: null,
  };

  @Component({
    components: {
      StatisticEmployeesV2,
      StatisticOrderSummariesTable,
      StatisticReports,
      StatisticPerformance,
    },
  })
  export default class Statistic extends Vue {
    summaries: OrderSummaries | null = null;
    summariesPreviousYear: OrderSummaries | null = null;

    private active = 0;
    activeType = this.typeAll;

    pagination: any = {
      sortBy: "name",
      rowsPerPage: 10,
    };

    @Getter(HeaderGetters.GET_MONTH, {namespace: HeaderModule})
    private month: string;

    @Watch("month")
    onDateChanged() {
      if (this.active === 0) {
        this.getOrderSummaries();
      }
    }

    get from(): Moment {
      return this.$moment(this.month).subtract(2, "months");
    }

    get to(): Moment {
      return this.$moment(this.month);
    }

    get isOverview(): boolean {
      return this.activeType.code === "ALL";
    }

    mounted() {
      this.getOrderSummaries();
    }

    get activeGroup(): OrderSummariesOrderTypeGroup | undefined {
      return this.summaries?.orderTypes.find((group) => group.orderType === this.activeType.code);
    }

    get activeGroupPreviousYear(): OrderSummariesOrderTypeGroup | undefined {
      return this.summariesPreviousYear?.orderTypes.find((group) => group.orderType === this.activeType.code);
    }

    get activeItems() {
      if (!this.summaries) {
        return [];
      }
      if (this.isOverview) {
        return this.summaries.orderTypes.map((group) => {
          const orderType = OrderTypes.find((type) => type.code === group.orderType);
          return toRow({name: orderType ? orderType.localizedName : group.orderType}, group.metrics);
        }).sort(function (a, b) {
          return a.order.name.localeCompare(b.order.name);
        });
      } else {
        const orders = this.activeGroup ? this.activeGroup.orders : [];
        return orders.map((line) => toRow({
          uuid: line.id,
          name: line.name,
          city: line.city,
        }, line.metrics));
      }
    }

    get activeTotal() {
      const metrics = this.isOverview ? this.summaries?.metrics : this.activeGroup?.metrics;
      return toRow(null, metrics ?? EMPTY_METRICS);
    }

    get activeComparisonTotal() {
      const metrics = this.isOverview ? this.summariesPreviousYear?.metrics : this.activeGroupPreviousYear?.metrics;
      return toRow(null, metrics ?? EMPTY_METRICS);
    }

    get typeAll() {
      return {code: "ALL", name: "Gesamt"};
    }

    get availableTypes() {
      const availableTypes = [this.typeAll];

      const orderTypes = OrderTypes.map((orderType) => ({
        code: orderType.code,
        name: orderType.localizedName,
      })).sort(function (a, b) {
        return a.name.localeCompare(b.name);
      });

      return availableTypes.concat(orderTypes);
    }

    getOrderSummaries() {
      const month = this.month;
      const sameMonthLastYear = this.$moment(month, "YYYY-MM").subtract(12, "months").format("YYYY-MM");
      StatisticServiceV2.getOrderSummaries(month).then((response) => {
        // Ignore responses for a month that is no longer selected
        if (month === this.month) {
          this.summaries = response.data;
        }
      });
      StatisticServiceV2.getOrderSummaries(sameMonthLastYear).then((response) => {
        if (month === this.month) {
          this.summariesPreviousYear = response.data;
        }
      });
    }

    itemClicked(entry: any) {
      if (entry.order.uuid) {
        this.$router.push({
          name: "StatisticOrder",
          params: {orderUuid: entry.order.uuid},
        });
      } else {
        const matchingType = this.availableTypes.find(
          (availableType) => availableType.name === entry.order.name
        );
        if (matchingType) {
          this.activeType = matchingType;
        }
      }
    }
  }
